/**
 * A mark-up drawing, read back.
 *
 * The answer is the strokes that made it, not a picture, so this redraws them
 * over the same face at whatever size the panel gives it. That is the whole
 * reason for storing strokes: a flattened screenshot would be far larger and
 * would blur on a retina screen, and this way the marks stay sharp and the
 * record stays small.
 */
import { useEffect, useRef, useState } from "react";

type Stroke = [number, number][];

export function DiagramAnswer({ value, which }: { value: unknown; which?: string }) {
  const canvas = useRef<HTMLCanvasElement | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const src = `/api/public/diagram/${which === "face-side" ? "face-side" : "face-front"}.png`;

  let strokes: Stroke[] = [];
  if (typeof value === "string" && value.trim() !== "") {
    try {
      const parsed: unknown = JSON.parse(value);
      if (Array.isArray(parsed)) strokes = parsed as Stroke[];
    } catch { /* an answer we cannot read draws as no marks, rather than crashing */ }
  }

  useEffect(() => {
    if (!size) return;
    const cv = canvas.current;
    const ctx = cv?.getContext("2d");
    if (!cv || !ctx) return;
    cv.width = size.w;
    cv.height = size.h;
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.strokeStyle = "#f43f5e";
    ctx.lineWidth = Math.max(3, cv.width / 140);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    for (const st of strokes) {
      if (!st?.length) continue;
      const first = st[0];
      if (!first) continue;
      ctx.beginPath();
      ctx.moveTo(first[0], first[1]);
      for (const pt of st.slice(1)) ctx.lineTo(pt[0], pt[1]);
      if (st.length === 1) ctx.lineTo(first[0] + 0.1, first[1] + 0.1);
      ctx.stroke();
    }
  }, [size, value]);

  if (strokes.length === 0) {
    return <p className="mt-0.5 text-sm text-zinc-500">Nothing marked</p>;
  }

  return (
    <div
      data-diagram-answer
      className="relative mt-1 w-full max-w-[260px] overflow-hidden rounded-lg border border-zinc-800 bg-white"
    >
      <img
        src={src}
        alt=""
        className="block h-auto w-full"
        onLoad={(e) => setSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
      />
      <canvas ref={canvas} className="absolute inset-0 h-full w-full" />
    </div>
  );
}
